import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";
import { DISCARDED_SUBITEMS_KEYS } from "../components/StaffManagement/Roles/constants";

const ROLES_QUERY_KEY = "ROLES";
const PERMISSIONS_SCHEMA_QUERY_KEY = "ROLES_PERMISSIONS_SCHEMA";
const STAFF_ROLES_DROPDOWN_QUERY_KEY = "GET_STAFF_ROLES_DROPDOWN";


function getClient() {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

/**
 * Recursively prefix each item key with the given prefix.
 * e.g. prefix "sportsbook" + item key "reporting" -> "sportsbook.reporting"
 */
function prefixItems(items, prefix) {
  if (!items?.length) return [];
  const prefixWithDot = `${prefix}.`;
  return items.map((item) => {
    let segmentKey = item.key;
    if (typeof segmentKey === "string" && segmentKey.startsWith(prefixWithDot)) {
      segmentKey = segmentKey.slice(prefixWithDot.length);
    }
    const prefixedKey = `${prefix}.${segmentKey}`;
    return {
      ...item,
      key: prefixedKey,
      display_name: item.display_name,
      items: item.items?.length ? prefixItems(item.items, prefixedKey) : item.items,
    };
  });
}

/**
 * For a dotted key like "a.b.c.d", find the deepest existing ancestor in the
 * schema. Tries "a.b.c", "a.b", "a" in order. Returns the matched parent and
 * the remainder as the child key.
 */
function findDeepestParent(byKey, dottedKey) {
  const parts = dottedKey.split(".");
  while (parts.length > 1) {
    parts.pop();
    const candidate = parts.join(".");
    if (byKey.has(candidate)) {
      return { parentKey: candidate, childKey: dottedKey.slice(candidate.length + 1) };
    }
  }
  return null;
}

/**
 * Some categories come back from the API with a dotted category_key
 * (e.g. "staff_management.agent_audit_log") which is really a sub-item
 * mis-modeled as a top-level category. Merge those into their parent.
 *
 * Behavior is controlled by `discardSubitemsSet`:
 * - If the merged key IS in the set, drop its sub-items (collapse to a single
 *   toggle) and record them in `discarded_subitems` so the save path can
 *   re-expand them when sending to the API.
 * - Otherwise, preserve sub-items as nested children.
 *
 * Returns { categories, discarded_subitems }.
 *   discarded_subitems shape: { [parentDottedKey]: [childKey1, childKey2, ...] }
 */
function normalizeMisnestedCategories(categories, discardSubitemsSet) {
  // Defensive clone — never mutate React Query's cached objects
  const cloned = categories.map((c) => ({ ...c, items: [...(c.items ?? [])] }));
  const byKey = new Map(cloned.map((c) => [c.category_key, c]));
  const result = [];
  const discarded_subitems = {};

  cloned.forEach((cat) => {
    if (!cat.category_key || !cat.category_key.includes(".")) {
      result.push(cat);
      return;
    }
    const found = findDeepestParent(byKey, cat.category_key);
    if (!found) {
      // No ancestor in the schema, keep it as-is so we don't lose it
      result.push(cat);
      return;
    }
    const { parentKey, childKey } = found;
    const parent = byKey.get(parentKey);

    // Collision guard — don't add duplicates if the parent already has this child
    if (parent.items.some((it) => it.key === childKey)) return;

    if (discardSubitemsSet.has(cat.category_key)) {
      // Collapse to a single toggle, remember the sub-items for save-time expansion
      parent.items.push({ key: childKey, display_name: cat.display_name });
      discarded_subitems[cat.category_key] = (cat.items ?? []).map((it) => it.key);
    } else {
      // Preserve sub-items as nested children
      parent.items.push({
        key: childKey,
        display_name: cat.display_name,
        items: cat.items ?? [],
      });
    }
  });

  return { categories: result, discarded_subitems };
}

// Exported for unit testing — internal helpers, not intended for app code
export { findDeepestParent, normalizeMisnestedCategories };

/**
 * GET permissions schema. Returns { available_permissions, available_actions }.
 * Response may be in response.data.body or response.data.
 */
async function fetchPermissionsSchema() {
  const client = getClient();
  const res = await client.get(API_ROUTES.ROLES_PERMISSIONS_SCHEMA);
  const body = res?.data?.body ?? res?.data ?? {};

  const discardSet = new Set(DISCARDED_SUBITEMS_KEYS);
  const misnested = normalizeMisnestedCategories(body.available_permissions ?? [], discardSet);
  const { discarded_subitems } = misnested;
  const normalized = misnested.categories;

  const baseAvailablePermissions = normalized.map((a) => {
    //Need to do this because the data is not standarized
    if (a.items?.length > 0) {
      return {
        ...a,
        items: prefixItems(a.items, a.category_key),
      };
    }
    return a;
  });

  const available_permissions = [...baseAvailablePermissions];
  const available_actions = body.available_actions ??  [];

  return {
    available_permissions,
    available_actions,
    discarded_subitems,
  };
}

/**
 * GET all roles. Returns { roles: [] }.
 */
async function fetchRoles() {
  const client = getClient();
  const res = await client.get(API_ROUTES.ROLES_LIST);
  const body = res?.data?.body ?? res?.data ?? {};
  const roles = body.roles ?? [];

  return Array.isArray(roles) ? roles : [];
}

/**
 * GET single role by id. Returns role object (may include etag).
 * Used to obtain etag for update when list response doesn't include it.
 */
async function fetchRole(roleId) {
  const client = getClient();
  const url = API_ROUTES.ROLE_GET.replace("{role_id}", roleId);
  const res = await client.get(url);
  const body = res?.data?.body ?? res?.data ?? {};
  const role = body.role ?? body;
  const etagFromHeader = res?.headers?.["etag"] ?? res?.headers?.["ETag"];
  if (role && etagFromHeader) role.etag = etagFromHeader;
  return role;
}

/**
 * POST create role. Payload: { name, color, permissions: string[], actions: string[], has_all_permissions?: boolean }.
 * Returns created role (response.data.body.role or response.data.role).
 */
async function createRole(payload) {
  const client = getClient();
  const res = await client.post(API_ROUTES.ROLE_CREATE, payload);
  const body = res?.data?.body ?? res?.data ?? {};
  return body.role ?? body;
}

/**
 * PUT update role. Params: { roleId, payload }. Payload: { name?, color?, permissions?, actions?, has_all_permissions?, etag }.
 * Returns updated role.
 */
async function updateRole({ roleId, payload }) {
  const client = getClient();
  const url = API_ROUTES.ROLE_UPDATE.replace("{role_id}", roleId);
  const res = await client.put(url, payload);
  const body = res?.data?.body ?? res?.data ?? {};
  return body.role ?? body;
}

/**
 * DELETE role. Returns { message, deleted_role_id }.
 */
async function deleteRole(roleId) {
  const client = getClient();
  const url = API_ROUTES.ROLE_DELETE.replace("{role_id}", roleId);
  const res = await client.delete(url);
  const body = res?.data?.body ?? res?.data ?? {};
  return body;
}

/**
 * POST copy role. Params: { roleId, new_name, new_color? }.
 * Returns created role.
 */
async function copyRole({ roleId, new_name, new_color }) {
  const client = getClient();
  const url = API_ROUTES.ROLE_COPY.replace("{role_id}", roleId);
  const res = await client.post(url, { new_name, new_color });
  const body = res?.data?.body ?? res?.data ?? {};
  return body.role ?? body;
}

export function useGetPermissionsSchema(options = {}) {
  return useQuery({
    queryKey: [PERMISSIONS_SCHEMA_QUERY_KEY],
    queryFn: fetchPermissionsSchema,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

export function useGetRoles(options = {}) {
  return useQuery({
    queryKey: [ROLES_QUERY_KEY],
    queryFn: fetchRoles,
    staleTime: 1000 * 60,
    ...options,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_ROLES_DROPDOWN_QUERY_KEY] });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_ROLES_DROPDOWN_QUERY_KEY] });
    },
  });
}

export function useCopyRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: copyRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
    },
  });
}

export { fetchRole };
